<template>
  <v-dialog
    v-model="dialogProxy"
    max-width="820px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Reverse Invoice Settings</span>
        <v-spacer />

        <v-chip
          v-if="originalDocument && originalDocument.document_number"
          color="error"
          outlined
          small
        >
          {{ originalDocument.document_number }}
        </v-chip>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <v-alert
          dense
          outlined
          type="warning"
          class="mb-5"
        >
          This action will generate a reverse invoice in Számlázz.hu. It cannot be undone.
        </v-alert>

        <v-form
          ref="form"
          v-model="isValid"
          lazy-validation
        >
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                :value="originalDocument ? originalDocument.document_number : '-'"
                label="Original invoice number"
                outlined
                dense
                readonly
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="documentTypeLabel(originalDocument)"
                label="Original document type"
                outlined
                dense
                readonly
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="formatAmount(originalDocument ? originalDocument.net_amount : null)"
                label="Original net amount"
                outlined
                dense
                readonly
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="formatAmount(originalDocument ? originalDocument.gross_amount : null)"
                label="Original gross amount"
                outlined
                dense
                readonly
              />
            </v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="12" md="6">
              <v-menu
                v-model="dateMenus.issueDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="localForm.issueDate"
                    label="Reverse invoice issue date"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="localForm.issueDate = null"
                  />
                </template>

                <v-date-picker
                  v-model="localForm.issueDate"
                  no-title
                  @input="dateMenus.issueDate = false"
                />
              </v-menu>

              <div class="d-flex flex-wrap mt-1 mb-2">
                <v-btn text small class="mr-2" @click="setTodayPlusDays('issueDate', 0)">Today</v-btn>
                <v-btn text small class="mr-2" @click="setTodayPlusDays('issueDate', 1)">+1</v-btn>
                <v-btn text small class="mr-2" @click="setTodayPlusDays('issueDate', 2)">+2</v-btn>
                <v-btn text small class="mr-2" @click="setTodayPlusDays('issueDate', 5)">+5</v-btn>
                <v-btn text small @click="setTodayPlusDays('issueDate', 8)">+8</v-btn>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <v-menu
                v-model="dateMenus.fulfillmentDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="localForm.fulfillmentDate"
                    label="Date of completion"
                    outlined
                    dense
                    readonly
                    clearable
                    hint="Optional. Leave empty to let backend use the original/default date."
                    persistent-hint
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="localForm.fulfillmentDate = null"
                  />
                </template>

                <v-date-picker
                  v-model="localForm.fulfillmentDate"
                  no-title
                  @input="dateMenus.fulfillmentDate = false"
                />
              </v-menu>

              <div class="d-flex flex-wrap mt-1 mb-2">
                <v-btn text small class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 0)">Today</v-btn>
                <v-btn text small class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 1)">+1</v-btn>
                <v-btn text small class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 2)">+2</v-btn>
                <v-btn text small class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 5)">+5</v-btn>
                <v-btn text small @click="setTodayPlusDays('fulfillmentDate', 8)">+8</v-btn>
              </div>
            </v-col>
          </v-row>

          <v-textarea
            v-model="localForm.reason"
            label="Reverse reason / comment"
            outlined
            dense
            auto-grow
            rows="4"
            counter="1000"
            hint="This text will be sent as the reverse invoice comment."
            persistent-hint
          />

          <v-expansion-panels
            v-model="advancedPanel"
            flat
            class="mt-4"
          >
            <v-expansion-panel>
              <v-expansion-panel-header>
                Advanced Számlázz.hu settings
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="localForm.isElectronicInvoice"
                      label="Electronic invoice"
                      inset
                      hide-details
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select
                      v-model="localForm.invoiceTemplate"
                      :items="invoiceTemplateItems"
                      item-text="text"
                      item-value="value"
                      label="Invoice template"
                      outlined
                      dense
                      clearable
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>
                </v-row>

                <v-divider class="my-4" />

                <div class="subtitle-2 mb-2">Buyer data override</div>

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="localForm.buyerEmail"
                      label="Buyer email"
                      outlined
                      dense
                      type="email"
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="localForm.buyerTaxNumber"
                      label="Buyer tax number"
                      outlined
                      dense
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="localForm.buyerEuTaxNumber"
                      label="Buyer EU tax number"
                      outlined
                      dense
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>
                </v-row>

                <v-divider class="my-4" />

                <div class="subtitle-2 mb-2">Email settings</div>

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="localForm.emailReplyTo"
                      label="Reply-to email"
                      outlined
                      dense
                      type="email"
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="localForm.emailSubject"
                      label="Email subject"
                      outlined
                      dense
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-model="localForm.emailText"
                      label="Email text"
                      outlined
                      dense
                      auto-grow
                      rows="3"
                      hint="Optional"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />

        <v-btn
          text
          :disabled="loading"
          @click="closeDialog"
        >
          Cancel
        </v-btn>

        <v-btn
          color="error"
          :loading="loading"
          @click="submit"
        >
          Reverse invoice
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from "sweetalert2";

const createReverseInvoiceForm = () => ({
  reason: null,
  issueDate: null,
  fulfillmentDate: null,
  isElectronicInvoice: false,
  invoiceTemplate: null,
  buyerEmail: null,
  buyerTaxNumber: null,
  buyerEuTaxNumber: null,
  emailReplyTo: null,
  emailSubject: null,
  emailText: null,
});

export default {
  name: "ReverseInvoiceDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    document: {
      type: Object,
      default: () => null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    seller: {
      type: Object,
      default: () => ({
        name: "Kismetál Kft.",
        email: null,
      }),
    },
    currency: {
      type: String,
      default: "HUF",
    },
  },

  data() {
    return {
      isValid: false,
      advancedPanel: null,
      dateMenus: {
        issueDate: false,
        fulfillmentDate: false,
      },
      localForm: createReverseInvoiceForm(),
      reasonRules: [
        (value) => !!String(value || "").trim() || "Reverse reason is required.",
        (value) => String(value || "").length <= 1000 || "Maximum 1000 characters.",
      ],
      invoiceTemplateItems: [
        { text: "Default", value: null },
        { text: "SzlaMost", value: "SzlaMost" },
        { text: "SzlaAlap", value: "SzlaAlap" },
        { text: "SzlaNoEnv", value: "SzlaNoEnv" },
        { text: "Szla8cm", value: "Szla8cm" },
        { text: "SzlaTomb", value: "SzlaTomb" },
        { text: "SzlaFuvarlevelesAlap", value: "SzlaFuvarlevelesAlap" },
      ],
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },

    originalDocument() {
      return this.document || null;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.syncForm();
        }
      },
    },

    document: {
      deep: true,
      handler() {
        if (this.value) {
          this.syncForm();
        }
      },
    },
  },

  methods: {
    syncForm() {
      const document = this.originalDocument || {};
      const documentNumber = document.document_number || "";

      this.localForm = {
        ...createReverseInvoiceForm(),
        issueDate: this.formatDateForInput(new Date()),
        fulfillmentDate: this.toInputDate(
          document.fulfillment_date ||
            document.issued_at ||
            document.created_at ||
            null
        ),
        isElectronicInvoice: this.isElectronicDocument(document),
        emailReplyTo: this.seller && this.seller.email ? this.seller.email : null,
        emailSubject: documentNumber
          ? `Sztornó számla - ${documentNumber}`
          : null,
      };

      this.advancedPanel = null;

      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    isElectronicDocument(document) {
      if (!document || !document.document_number) {
        return false;
      }

      return String(document.document_number).indexOf("E-") === 0;
    },

    documentTypeLabel(document) {
      if (!document || !document.type) {
        return "-";
      }

      const type = String(document.type).toLowerCase();

      if (type === "invoice") return "Invoice";
      if (type === "deposit_invoice") return "Deposit Invoice";
      if (type === "final_invoice") return "Final Invoice";

      return String(document.type).replace(/_/g, " ");
    },

    formatAmount(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return value;
      }

      if (this.$helpers && this.$helpers.getNicePrice) {
        return this.$helpers.getNicePrice(numericValue, this.currency);
      }

      return `${numericValue} ${this.currency}`;
    },

    toInputDate(value) {
      if (!value) {
        return null;
      }

      const normalized = String(value).replace(" ", "T");
      const date = new Date(normalized);

      if (Number.isNaN(date.getTime())) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
          return String(value);
        }

        return null;
      }

      return this.formatDateForInput(date);
    },

    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    setTodayPlusDays(field, days) {
      const date = new Date();

      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() + days);

      this.localForm[field] = this.formatDateForInput(date);
    },

    closeDialog() {
      this.syncForm();
      this.dialogProxy = false;
    },

    buildPayload() {
      return {
        documentId: this.originalDocument ? this.originalDocument.id : null,
        reason: this.localForm.reason || null,
        issueDate: this.localForm.issueDate || null,
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        isElectronicInvoice: this.localForm.isElectronicInvoice ? 1 : 0,
        invoiceTemplate: this.localForm.invoiceTemplate || null,
        buyerEmail: this.localForm.buyerEmail || null,
        buyerTaxNumber: this.localForm.buyerTaxNumber || null,
        buyerEuTaxNumber: this.localForm.buyerEuTaxNumber || null,
        emailReplyTo: this.localForm.emailReplyTo || null,
        emailSubject: this.localForm.emailSubject || null,
        emailText: this.localForm.emailText || null,
      };
    },

    async submit() {
      if (!this.$refs.form || !this.$refs.form.validate()) {
        return;
      }

      if (!this.originalDocument || !this.originalDocument.id) {
        return;
      }

      const result = await Swal.fire({
        title: "Reverse invoice?",
        text: "Are you sure you want to reverse this invoice? This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, reverse it",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e33354",
        reverseButtons: true,
        focusCancel: true,
      });

      const confirmed =
        result &&
        (result.value === true || result.isConfirmed === true);

      if (!confirmed) {
        return;
      }

      this.$emit("save", this.buildPayload());
    },
  },
};
</script>